import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Divider,
  Grid,
  Stack,
  Typography,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Edit as EditIcon,
  PowerSettingsNew as PowerSettingsNewIcon,
  Settings as SettingsIcon,
  Sync as SyncIcon,
} from '@mui/icons-material';
import { Link, useParams } from 'react-router-dom';
import ConfirmDialog from '@/components/common/ConfirmDialog/ConfirmDialog';
import ReaderMqttConfigDialog from '@/components/rfid/ReaderMqttConfigDialog';
import rfidService, { Reader } from '@/services/rfidService';
import { connectReaderLiveConsole } from '@/services/readerLiveConsoleService';
import toast from '@/utils/toast';

const DetailField = ({ label, value }: { label: string; value: React.ReactNode }) => (
  <Grid item xs={12} md={6}>
    <Typography variant="subtitle2" color="text.secondary">{label}</Typography>
    <Typography sx={{ overflowWrap: 'anywhere' }}>{value || '—'}</Typography>
  </Grid>
);

const ReaderDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [reader, setReader] = useState<Reader | null>(null);
  const [loading, setLoading] = useState(true);
  const [configOpen, setConfigOpen] = useState(false);
  const [consoleMessage, setConsoleMessage] = useState('Disconnected. Connect to start receiving reader logs.');
  const [credentialsConfirmOpen, setCredentialsConfirmOpen] = useState(false);
  const [generatingCredentials, setGeneratingCredentials] = useState(false);

  useEffect(() => {
    if (!id) return;
    const load = async () => {
      try {
        setLoading(true);
        setReader(await rfidService.getReaderById(id));
      } catch (error) {
        console.error('Unable to load RFID reader', error);
        toast.error('Unable to load reader details');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [id]);

  const connectConsole = async () => {
    if (!reader) return;
    const result = await connectReaderLiveConsole(reader.id);
    setConsoleMessage(result.message);
  };

  const handleGenerateCredentials = async () => {
    if (!reader) return;
    try {
      setGeneratingCredentials(true);
      const updated = await rfidService.generateReaderMqttCredentials(reader.id);
      setReader(updated);
      toast.success('MQTT credentials generated');
      setConfigOpen(true);
    } catch (error) {
      console.error('Unable to generate MQTT credentials', error);
      toast.error('Unable to generate MQTT credentials');
    } finally {
      setGeneratingCredentials(false);
      setCredentialsConfirmOpen(false);
    }
  };

  if (loading) return <Box display="flex" justifyContent="center" p={3}><CircularProgress /></Box>;
  if (!reader) return <Alert severity="error">Reader not found.</Alert>;

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Stack direction="row" spacing={1} alignItems="center">
          <Button variant="outlined" startIcon={<ArrowBackIcon />} component={Link} to="/rfid/readers">Back</Button>
          <Typography variant="h4">{reader.name}</Typography>
        </Stack>
        <Stack direction="row" spacing={1}>
          <Button variant="outlined" startIcon={<SettingsIcon />} onClick={() => setConfigOpen(true)}>View Configuration</Button>
          <Button
            variant="outlined"
            color="warning"
            startIcon={<SyncIcon />}
            onClick={() => setCredentialsConfirmOpen(true)}
          >
            Revoke &amp; Generate
          </Button>
          <Button variant="contained" startIcon={<EditIcon />} component={Link} to={`/rfid/readers/${reader.id}/edit`}>Edit</Button>
        </Stack>
      </Box>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>Reader Details</Typography>
          <Divider sx={{ mb: 2 }} />
          <Grid container spacing={2}>
            <DetailField label="Device ID" value={reader.device_id} />
            <DetailField label="Reader Type" value={reader.reader_type_display ?? reader.reader_type} />
            <DetailField label="Location" value={reader.location_detail?.name} />
            <DetailField label="Description" value={reader.description} />
            <DetailField label="Last Seen" value={reader.last_seen ? new Date(reader.last_seen).toLocaleString() : 'Never'} />
            <DetailField label="Created" value={new Date(reader.created_at).toLocaleString()} />
            <DetailField label="Updated" value={new Date(reader.updated_at).toLocaleString()} />
          </Grid>
        </CardContent>
      </Card>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>Scan Settings</Typography>
          <Divider sx={{ mb: 2 }} />
          <Grid container spacing={2}>
            <DetailField label="Scan Interval" value={`${reader.scan_interval} seconds`} />
            <DetailField label="Debounce Time" value={`${reader.debounce_time} seconds`} />
            <DetailField label="Threshold Time" value={`${reader.threshold_time} seconds`} />
          </Grid>
        </CardContent>
      </Card>

      <Card sx={{ mb: 3, bgcolor: 'background.paper' }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>Live Console</Typography>
          <Typography variant="body2" color="text.secondary" mb={2}>Live reader logs will stream here when the WebSocket service is available.</Typography>
          <Box role="log" aria-live="polite" sx={{ minHeight: 120, bgcolor: '#f8fafc', color: 'text.primary', border: 1, borderColor: 'divider', borderRadius: 1, p: 2, fontFamily: 'monospace', whiteSpace: 'pre-wrap' }}>
            {consoleMessage}
          </Box>
          <Stack direction="row" spacing={1} mt={2}>
            <Button variant="contained" startIcon={<PowerSettingsNewIcon />} onClick={connectConsole}>Connect</Button>
            <Button variant="outlined" onClick={() => setConsoleMessage('Disconnected. Connect to start receiving reader logs.')}>Disconnect</Button>
          </Stack>
        </CardContent>
      </Card>

      <ReaderMqttConfigDialog open={configOpen} reader={reader} onClose={() => setConfigOpen(false)} />

      <ConfirmDialog
        open={credentialsConfirmOpen}
        title="Revoke & Generate MQTT Credentials"
        content="This immediately invalidates this reader's current MQTT username and password and issues a new pair. The reader device will need to be reconfigured with the new credentials before it can reconnect. Continue?"
        confirmText="Revoke & Generate"
        onConfirm={handleGenerateCredentials}
        onCancel={() => setCredentialsConfirmOpen(false)}
        isLoading={generatingCredentials}
      />
    </Box>
  );
};

export default ReaderDetail;
